import { AuthN, getConfig } from '@seek/online-identity';

import { hasKeyValue } from './validation';

let $authN: AuthN | undefined;

const getAuthN = (): AuthN => {
  if (!$authN) {
    const config = getConfig('seekAnz-candidate', {
      host: window.location.host,
      path: window.location.pathname,
    });

    $authN = new AuthN(config);
  }

  return $authN;
};

export const resetAuthN = (): void => ($authN = undefined);

/**
 * Run a function that requires authentication, redirecting to the login flow if
 * it fails with a known authentication error.
 *
 * {@link https://seek-auth-manual.ssod.skinfra.xyz/#/guides/spa.md#token-acquisition}
 */
const withLoginFallback = async <T>(
  fn: (authN: AuthN) => Promise<T>,
): Promise<T | void> => {
  const authN = getAuthN();

  try {
    return await fn(authN);
  } catch (err: unknown) {
    if (
      hasKeyValue(err, 'error', 'login_required') ||
      hasKeyValue(err, 'error', 'invalid_grant')
    ) {
      return authN.login();
    }

    throw err;
  }
};

export const authClient = {
  accessToken: () => withLoginFallback((authN) => authN.getToken()),

  user: () => withLoginFallback((authN) => authN.getUser()),

  login: () => getAuthN().login(),

  /**
   * Our `/api/iam/oauth2/authorize` and `/oauth2/debug` routes are configured
   * as allowed logout URLs in Auth0 config.
   *
   * - {@link https://github.com/SEEK-Jobs/online-identity-auth0-config/blob/master/tenants/dev/anz/seekanz/clients/seek_co_nz_staging.json}
   * - {@link https://github.com/SEEK-Jobs/online-identity-auth0-config/blob/master/tenants/dev/anz/seekanz/clients/seek_com_au_staging.json}
   * - {@link https://github.com/SEEK-Jobs/online-identity-auth0-config/blob/master/tenants/prod/anz/seekanz/clients/seek_co_nz.json}
   * - {@link https://github.com/SEEK-Jobs/online-identity-auth0-config/blob/master/tenants/prod/anz/seekanz/clients/seek_com_au.json}
   */
  logout: () => getAuthN().logout(window.location.href),
};
